import { z } from "zod";

import { type components, type operations } from "@/lib/api/schema";

export { ProcedureTypes } from "@punto/frontend-shared/constants/procedure-types";

export type Procedure = components["schemas"]["ProcedureDto"];
export type ProcedureDetail = components["schemas"]["ProcedureDetailDto"];

export type ProcedureForEdit = components["schemas"]["ProcedureForEdit"];
export type CustomerRoleOption = components["schemas"]["CustomerRoleOption"];

export type PreviousProcedure = components["schemas"]["PreviousProcedure"];

export type ProcedureVehicle = components["schemas"]["ProcedureVehicleDto"];
export type ProcedureStatus = components["schemas"]["ProcedureStatus"];

export type ProcedureType = components["schemas"]["ProcedureType"];

export type ProcedureBillingData = components["schemas"]["ProcedureBillingDto"];

export type PartyType = "person" | "company";

export enum IdTypeEnum {
  DNI = "DNI",
  NIE = "NIE",
  CIF = "CIF",
}

export enum StepStatus {
  processing = "processing",
  pending = "pending",
  success = "success",
  error = "error",
}

export enum TransferCheckStepResult {
  success = "success",
  error = "error",
}

export type UpdateParty = components["schemas"]["UpdatePartyDto"];
export type CreateParty = components["schemas"]["CreatePartyDto"];

export type Province = components["schemas"]["ProvinceDto"];
export type Municipality = components["schemas"]["MunicipalityDto"];

export type PartyRole = "SELLER" | "BUYER" | "DEALER" | "OWNER";

export enum ProcedureStatuses {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  DELIVERED_TO_DGT = "DELIVERED_TO_DGT",
  RECEIVED = "RECEIVED",
  DELIVERED_TO_CLIENT = "DELIVERED_TO_CLIENT",
  BLOCKED = "BLOCKED",
  ON_HOLD = "ON_HOLD",
  CANCELLED = "CANCELLED",
}

export enum ProcedureCustomerRoles {
  BUYER = "BUYER",
  SELLER = "SELLER",
  DEALER = "DEALER",
  NONE = "NONE",
}

export type ProcedureGroupType = components["schemas"]["ProcedureGroupType"];
export type PendingTaxGroup = components["schemas"]["PendingTaxGroupDto"];
export type PendingTaxGroupsResponse =
  components["schemas"]["PendingTaxGroupsResponseDto"];

export type IncidentGroup = components["schemas"]["IncidentGroupDto"];
export type IncidentGroupsResponse =
  components["schemas"]["IncidentGroupsResponseDto"];

/**
 * The two incident severities. Taken off the group DTO minus its `null` — the
 * catch-all "Otros" group has no level, but the `level` a view *asks to group
 * by* always does (it is a required query param since P-2849 § 4.1).
 */
export type IncidentLevel = NonNullable<IncidentGroup["level"]>;

export const DocumentsTableStateSchema = z.object({
  pagination: z.object({
    pageIndex: z.number(),
    pageSize: z.number(),
  }),
  sorting: z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  ),
  filters: z.optional(
    z.object({
      search: z.optional(z.string()),
    }),
  ),
});

export type DocumentsTableState = z.infer<typeof DocumentsTableStateSchema>;

/** Procedure Detail types */

export type ProcedureDetailBase = components["schemas"]["ProcedureDetail"];
export type ProcedureDetailVehicleBasicInfo =
  components["schemas"]["ProcedureDetailVehicleBasicInfo"];
export type ProcedureDetailVehicleServiceInfo =
  components["schemas"]["ProcedureDetailVehicleServiceInfo"];
export type ProcedureDetailVehicleTechnicalInfo =
  components["schemas"]["ProcedureDetailVehicleTechnicalInfo"] & {
    selectedVehicleModel?: TaxAuthorityVehicleMatch | null;
    vehicleModelCandidates?: TaxAuthorityVehicleMatch[] | null;
    modelMatchStatus?: string;
  };
export type ProcedureDetailVehicleInspectionInfo =
  components["schemas"]["ProcedureDetailVehicleInspectionInfo"];

export type ProcedureDetailVehicleData =
  components["schemas"]["ProcedureDetailVehicleData"];

/** P-2622: one cell of the registration condition × origin matrix. */
export type RegistrationOriginRule =
  components["schemas"]["RegistrationOriginRuleDto"];

export type ProcedureDetailData = components["schemas"]["ProcedureDetailData"];
export type PendingDocumentsData =
  components["schemas"]["PendingDocumentsData"];

export type ProcedureDetailTransferCheckData =
  components["schemas"]["ProcedureDetailTransferCheckData"];

export type ProcedureDetailInfo = components["schemas"]["ProcedureDetail"];

export type ProcedureDetailProcedureData =
  components["schemas"]["ProcedureDetailProcedureData"];

export type ProcedureDetailProcedureBasicInfo =
  components["schemas"]["ProcedureDetailProcedureBasicInfo"];

export type OperationResponse = components["schemas"]["OperationResponse"];

export type ProcedureDetailMetadata =
  components["schemas"]["ProcedureDetailMetadata"];

/** Party types */
export type PartyData = components["schemas"]["PartyData"];
export type PersonBasicInfo = components["schemas"]["PersonBasicInfo"];
export type CompanyBasicInfo = components["schemas"]["CompanyBasicInfo"];
export type PartySpecialFields = components["schemas"]["PartySpecialFields"];
export type PartyAddress = components["schemas"]["PartyAddress"];
export type PartyRepresentative = components["schemas"]["PartyRepresentative"];

/** Procedure data block types (P-3013) */
export type ParticipantBlockData =
  components["schemas"]["ParticipantBlockData"];
export type ParticipantBlockStatus =
  components["schemas"]["ParticipantBlockStatus"];
export type ProcedureParticipantBlock =
  components["schemas"]["ProcedureParticipantBlock"];
export type ProcedureDataDocuments =
  components["schemas"]["ProcedureDataDocuments"];
export type VehicleBlockData = components["schemas"]["VehicleBlockData"];
export type ProcedureVehicleBlock =
  components["schemas"]["ProcedureVehicleBlock"];
export type ProcedureProcedureBlockData =
  components["schemas"]["ProcedureProcedureBlockData"];
export type ProcedureProcedureBlock =
  components["schemas"]["ProcedureProcedureBlock"];
export type ProcedureDataDocumentsGroup =
  components["schemas"]["ProcedureDataDocumentsGroup"];
export type DocumentWithIncident =
  components["schemas"]["DocumentWithIncident"];

/**
 * Procedure Batch types
 */

export type ProcedureBatchParticipant =
  components["schemas"]["ProcedureBatchParticipant"];
export type ProcedureBatch = components["schemas"]["ProcedureBatch"];

export type ProcedureQuickView = components["schemas"]["ProcedureQuickViewDto"];
export type ProcedureBatchVehicle =
  components["schemas"]["ProcedureBatchVehicle"];

export type BatchesSortingValues =
  components["schemas"]["ProcedureBatchQueryDto"]["sort"];

export const BatchesTableStateSchema = z.object({
  pagination: z.object({
    pageIndex: z.number(),
    pageSize: z.number(),
  }),
  sorting: z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  ),
  filters: z.optional(z.object({})),
});

export type ProcedureBatchListItem =
  components["schemas"]["ProcedureBatchListItemDto"];

export interface CountQueryItem {
  id: string;
  query: NonNullable<operations["getProcedureCount"]["parameters"]["query"]>;
}

export type TaxAuthorityVehicleMatch =
  components["schemas"]["TaxAuthorityVehicleMatch"];
